import React, { useCallback, useState, createContext, useContext } from 'react';
// Types
export type ApprovalStep = {
  role: string;
  name: string;
  status: 'approved' | 'pending' | 'waiting';
};
export type TravelRequest = {
  id: string;
  requester: string;
  role: string;
  trip: string;
  destination: string;
  dates: string;
  amount: number;
  type: 'flight' | 'hotel' | 'car';
  details: string;
  status: 'pending' | 'approved' | 'rejected';
  policyStatus: 'compliant' | 'violation';
  submittedAt: string;
  chain: ApprovalStep[];
  policyFlags?: string[];
  justification?: string;
  delegatedTo?: string;
  delegationDates?: string;
  escalationHours?: number;
  approverComments?: string[];
};
export type Notification = {
  id: number;
  type: 'approval' | 'booking' | 'policy' | 'system' | 'marketing' | 'operational';
  title: string;
  desc: string;
  time: string;
  read: boolean;
  action: string | null;
  category?: 'operational' | 'marketing' | 'system';
};
export type ConfirmedTrip = {
  id: number;
  title: string;
  location: string;
  dates: string;
  status: 'Confirmed';
  totalCost: string;
};
export type AuditLog = {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  category: 'Approvals' | 'Reassignments' | 'Budget Overrides' | 'Ticket Issuance' | 'Refunds' | 'Delegations' | 'System';
  details: string;
  ip: string;
  metadata?: Record<string, string>;
};
export type SupportCase = {
  id: string;
  subject: string;
  category: 'Booking Issue' | 'Refund Request' | 'Policy Question' | 'Technical' | 'Other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
  linkedBooking?: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  assignedTo?: string;
  requester: string;
  slaResponseTime: string;
  slaResolutionTime: string;
  timeline: {
    time: string;
    action: string;
    user: string;
  }[];
};
export type EventItem = {
  id: string;
  title: string;
  description: string;
  date: string;
  endDate: string;
  location: string;
  category: 'Conference' | 'Training' | 'Summit' | 'Partner Event';
  industry: string[];
  image: string;
  attendeeCount: number;
  interested: boolean;
  featured: boolean;
};
export type PaymentInfo = {
  availableCredit: number;
  usedCredit: number;
  creditLimit: number;
  pendingPayments: number;
  totalSpend: number;
};
export type Integration = {
  id: string;
  name: string;
  type: 'booking_engine' | 'ground_transport';
  status: 'connected' | 'disconnected' | 'configuring';
  lastSync: string;
  logo: string;
};
export type RefundPolicy = {
  id: string;
  service: 'Flights' | 'Hotels' | 'Insurance' | 'Ground Transport' | 'Ancillary';
  refundable: boolean;
  cancellationRules: string;
  penalties: string;
  timeLimit: string;
  conditions: string;
};
export type Delegation = {
  id: string;
  approver: string;
  delegate: string;
  startDate: string;
  endDate: string;
  active: boolean;
};
export type EscalationRules = {
  warningHours: number;
  escalateHours: number;
  autoEscalateEnabled: boolean;
};
export type OperationsQueue = {
  pendingApprovals: number;
  awaitingTicketing: number;
  ticketed: number;
  refunds: number;
  cancellations: number;
};
interface AppContextType {
  travelRequests: TravelRequest[];
  notifications: Notification[];
  confirmedTrips: ConfirmedTrip[];
  auditLogs: AuditLog[];
  supportCases: SupportCase[];
  events: EventItem[];
  paymentInfo: PaymentInfo;
  integrations: Integration[];
  refundPolicies: RefundPolicy[];
  delegations: Delegation[];
  escalationRules: EscalationRules;
  operationsQueue: OperationsQueue;
  submitBooking: (booking: any) => void;
  approveRequest: (requestId: string) => void;
  rejectRequest: (requestId: string) => void;
  markNotificationRead: (id: number) => void;
  markAllNotificationsRead: () => void;
  addAuditLog: (log: Omit<AuditLog, 'id' | 'timestamp' | 'ip'>) => void;
  toggleEventInterest: (eventId: string) => void;
}
const AppContext = createContext<AppContextType | undefined>(undefined);
export function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [travelRequests, setTravelRequests] = useState<TravelRequest[]>([{
    id: 'REQ-2024-001',
    requester: 'David Miller',
    role: 'Senior Developer',
    trip: 'Tech Conference 2024',
    destination: 'San Francisco, CA',
    dates: 'Nov 12 - Nov 15',
    amount: 1850,
    type: 'flight',
    details: 'United Airlines',
    status: 'pending',
    policyStatus: 'violation',
    policyFlags: ['Flight cost exceeds cap by 10%'],
    submittedAt: '2 hours ago',
    escalationHours: 18,
    chain: [{
      role: 'Direct Manager',
      name: 'Sarah Connor',
      status: 'approved'
    }, {
      role: 'Dept Head',
      name: 'Alex Morgan',
      status: 'pending'
    }, {
      role: 'Finance',
      name: 'Emily Chen',
      status: 'waiting'
    }]
  }, {
    id: 'REQ-2024-002',
    requester: 'Sarah Jenkins',
    role: 'Sales Director',
    trip: 'Client Meeting',
    destination: 'Chicago, IL',
    dates: 'Nov 18 - Nov 20',
    amount: 950,
    type: 'hotel',
    details: 'Marriott Downtown',
    status: 'pending',
    policyStatus: 'compliant',
    policyFlags: [],
    submittedAt: '5 hours ago',
    escalationHours: 5,
    chain: [{
      role: 'Direct Manager',
      name: 'Mike Ross',
      status: 'pending'
    }, {
      role: 'Dept Head',
      name: 'Jessica Pearson',
      status: 'waiting'
    }, {
      role: 'Finance',
      name: 'Louis Litt',
      status: 'waiting'
    }]
  }, {
    id: 'REQ-2024-003',
    requester: 'Michael Chen',
    role: 'Product Manager',
    trip: 'Team Offsite',
    destination: 'Austin, TX',
    dates: 'Dec 05 - Dec 08',
    amount: 1200,
    type: 'flight',
    details: 'Delta Airlines',
    status: 'approved',
    policyStatus: 'compliant',
    policyFlags: [],
    submittedAt: '1 day ago',
    chain: [{
      role: 'Direct Manager',
      name: 'Sarah Connor',
      status: 'approved'
    }, {
      role: 'Dept Head',
      name: 'Alex Morgan',
      status: 'approved'
    }, {
      role: 'Finance',
      name: 'Emily Chen',
      status: 'approved'
    }]
  }]);
  const [notifications, setNotifications] = useState<Notification[]>([{
    id: 1,
    type: 'approval',
    title: 'Trip request approved',
    desc: 'Your request for Tech Conference has been approved by Sarah Connor',
    time: '2 hours ago',
    read: false,
    action: 'View Request',
    category: 'operational'
  }, {
    id: 2,
    type: 'booking',
    title: 'New booking confirmed',
    desc: 'Flight UA 4521 JFK→LHR confirmed for Nov 12',
    time: '5 hours ago',
    read: false,
    action: 'View Booking',
    category: 'operational'
  }, {
    id: 3,
    type: 'policy',
    title: 'Policy violation detected',
    desc: 'Hotel booking exceeds nightly cap by $45',
    time: '1 day ago',
    read: true,
    action: 'Review Policy',
    category: 'operational'
  }, {
    id: 4,
    type: 'system',
    title: 'Expense report approved',
    desc: 'Q3 Travel Expenses ($3,240) approved for reimbursement',
    time: '1 day ago',
    read: true,
    action: 'View Report',
    category: 'system'
  }, {
    id: 5,
    type: 'marketing',
    title: 'Pharma Innovation Summit 2024',
    desc: 'Join 500+ pharmaceutical leaders in Boston. Early bird registration ends soon!',
    time: '3 hours ago',
    read: false,
    action: 'View Event',
    category: 'marketing'
  }, {
    id: 6,
    type: 'marketing',
    title: 'Partner Offer: 20% off Emirates',
    desc: 'Exclusive corporate discount on Emirates business class flights through Dec 31',
    time: '6 hours ago',
    read: false,
    action: 'View Offer',
    category: 'marketing'
  }, {
    id: 7,
    type: 'operational',
    title: 'Flight delay notification',
    desc: 'UA 4521 delayed by 45 minutes. New departure: 08:45 AM',
    time: '30 min ago',
    read: false,
    action: 'View Details',
    category: 'operational'
  }]);
  const [confirmedTrips, setConfirmedTrips] = useState<ConfirmedTrip[]>([{
    id: 1,
    title: 'Q4 Strategy Meeting',
    location: 'San Francisco, CA',
    dates: 'Nov 12 - Nov 15, 2023',
    status: 'Confirmed',
    totalCost: '$2,003.00'
  }]);
  const [auditLogs] = useState<AuditLog[]>([{
    id: 'LOG-001',
    timestamp: '2024-11-10 14:32:05',
    user: 'Alex Morgan',
    action: 'Approved travel request REQ-2024-001',
    category: 'Approvals',
    details: "Approved step 2 of 3 for David Miller's Tech Conference trip",
    ip: '192.168.1.45'
  }, {
    id: 'LOG-002',
    timestamp: '2024-11-10 13:15:22',
    user: 'Admin System',
    action: 'Reassigned approval from John Doe to Sarah Connor',
    category: 'Reassignments',
    details: 'John Doe marked as unavailable. Reason: Out of office',
    ip: '10.0.0.1'
  }, {
    id: 'LOG-003',
    timestamp: '2024-11-10 11:45:00',
    user: 'Emily Chen',
    action: 'Budget override for Product department',
    category: 'Budget Overrides',
    details: 'Override amount: $5,000. Reason: Q4 conference attendance required',
    ip: '192.168.1.78'
  }, {
    id: 'LOG-004',
    timestamp: '2024-11-09 16:20:33',
    user: 'System',
    action: 'Ticket issued for REQ-2024-003',
    category: 'Ticket Issuance',
    details: 'Delta Airlines DL-4892, Austin TX. PNR: XKJF82',
    ip: '10.0.0.1'
  }, {
    id: 'LOG-005',
    timestamp: '2024-11-09 14:10:15',
    user: 'Lisa Park',
    action: 'Refund processed for BK-7801',
    category: 'Refunds',
    details: 'Full refund of $450 for cancelled flight. Refund ID: RF-2024-089',
    ip: '192.168.1.92'
  }, {
    id: 'LOG-006',
    timestamp: '2024-11-09 10:05:44',
    user: 'Alex Morgan',
    action: 'Delegated approvals to Mike Ross',
    category: 'Delegations',
    details: 'Delegation period: Nov 15 - Nov 22. All pending approvals transferred.',
    ip: '192.168.1.45'
  }, {
    id: 'LOG-007',
    timestamp: '2024-11-08 09:30:00',
    user: 'System',
    action: 'Automated escalation triggered',
    category: 'System',
    details: 'REQ-2024-001 escalated after 24h without action. Notified department head.',
    ip: '10.0.0.1'
  }, {
    id: 'LOG-008',
    timestamp: '2024-11-08 08:00:00',
    user: 'Sarah Connor',
    action: 'Approved travel request REQ-2024-001 (Step 1)',
    category: 'Approvals',
    details: "Manager approval for David Miller's Tech Conference trip. Amount: $1,850",
    ip: '192.168.1.33'
  }, {
    id: 'LOG-009',
    timestamp: '2024-11-07 15:45:12',
    user: 'Finance Bot',
    action: 'Budget alert triggered',
    category: 'System',
    details: 'Product department reached 93% budget utilization. Alert sent to finance team.',
    ip: '10.0.0.1'
  }, {
    id: 'LOG-010',
    timestamp: '2024-11-07 11:20:00',
    user: 'James Wilson',
    action: 'Ticket issued for BK-7830',
    category: 'Ticket Issuance',
    details: 'United Airlines UA-2201, New York JFK. PNR: MNOP45',
    ip: '192.168.1.56'
  }]);
  const [supportCases] = useState<SupportCase[]>([{
    id: 'CASE-001',
    subject: 'Flight rebooking needed due to schedule change',
    category: 'Booking Issue',
    priority: 'high',
    status: 'open',
    linkedBooking: 'BK-7829',
    description: 'United Airlines changed the departure time. Need to rebook to an earlier flight.',
    createdAt: '2 hours ago',
    updatedAt: '1 hour ago',
    assignedTo: 'Support Team',
    requester: 'Sarah Connor',
    slaResponseTime: '2h remaining',
    slaResolutionTime: '22h remaining',
    timeline: [{
      time: '2 hours ago',
      action: 'Case created',
      user: 'Sarah Connor'
    }, {
      time: '1 hour ago',
      action: 'Assigned to Support Team',
      user: 'System'
    }]
  }, {
    id: 'CASE-002',
    subject: 'Refund not received for cancelled hotel',
    category: 'Refund Request',
    priority: 'medium',
    status: 'in_progress',
    linkedBooking: 'BK-7815',
    description: 'Cancelled hotel booking 2 weeks ago but refund has not been processed.',
    createdAt: '1 day ago',
    updatedAt: '6 hours ago',
    assignedTo: 'Lisa Park',
    requester: 'David Miller',
    slaResponseTime: 'Met',
    slaResolutionTime: '18h remaining',
    timeline: [{
      time: '1 day ago',
      action: 'Case created',
      user: 'David Miller'
    }, {
      time: '20 hours ago',
      action: 'Assigned to Lisa Park',
      user: 'System'
    }, {
      time: '6 hours ago',
      action: 'Contacted hotel for refund status',
      user: 'Lisa Park'
    }]
  }, {
    id: 'CASE-003',
    subject: 'Need visa guidance for Japan trip',
    category: 'Policy Question',
    priority: 'low',
    status: 'resolved',
    description: 'Planning a business trip to Tokyo. Need guidance on visa requirements for US passport holders.',
    createdAt: '3 days ago',
    updatedAt: '2 days ago',
    assignedTo: 'Travel Desk',
    requester: 'Michael Chen',
    slaResponseTime: 'Met',
    slaResolutionTime: 'Met',
    timeline: [{
      time: '3 days ago',
      action: 'Case created',
      user: 'Michael Chen'
    }, {
      time: '3 days ago',
      action: 'Assigned to Travel Desk',
      user: 'System'
    }, {
      time: '2 days ago',
      action: 'Provided visa guidance documentation',
      user: 'Travel Desk'
    }, {
      time: '2 days ago',
      action: 'Case resolved',
      user: 'Michael Chen'
    }]
  }]);
  const [events, setEvents] = useState<EventItem[]>([{
    id: 'EVT-001',
    title: 'Pharma Innovation Summit 2024',
    description: 'Join 500+ pharmaceutical leaders to discuss the future of drug development, clinical trials, and regulatory compliance.',
    date: 'Dec 10, 2024',
    endDate: 'Dec 12, 2024',
    location: 'Boston, MA',
    category: 'Conference',
    industry: ['Pharmaceutical', 'Healthcare'],
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=600&q=80',
    attendeeCount: 342,
    interested: false,
    featured: true
  }, {
    id: 'EVT-002',
    title: 'Global Tech Leaders Forum',
    description: 'Annual gathering of CIOs and CTOs from Fortune 500 companies. Keynotes on AI, cloud infrastructure, and digital transformation.',
    date: 'Jan 15, 2025',
    endDate: 'Jan 17, 2025',
    location: 'Las Vegas, NV',
    category: 'Summit',
    industry: ['Technology', 'Finance'],
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=600&q=80',
    attendeeCount: 528,
    interested: true,
    featured: true
  }, {
    id: 'EVT-003',
    title: 'Corporate Travel Management Workshop',
    description: 'Hands-on training for travel managers on policy optimization, cost reduction, and traveler satisfaction.',
    date: 'Nov 28, 2024',
    endDate: 'Nov 29, 2024',
    location: 'Chicago, IL',
    category: 'Training',
    industry: ['Travel', 'Corporate'],
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=600&q=80',
    attendeeCount: 89,
    interested: false,
    featured: false
  }, {
    id: 'EVT-004',
    title: 'FinTech World Congress',
    description: 'Explore the intersection of finance and technology. Topics include blockchain, digital payments, and regulatory tech.',
    date: 'Feb 05, 2025',
    endDate: 'Feb 07, 2025',
    location: 'London, UK',
    category: 'Conference',
    industry: ['Finance', 'Technology'],
    image: 'https://images.unsplash.com/photo-1559223607-a43c990c692c?auto=format&fit=crop&w=600&q=80',
    attendeeCount: 415,
    interested: false,
    featured: false
  }, {
    id: 'EVT-005',
    title: 'Airline Partner Showcase',
    description: 'Meet our airline partners and learn about exclusive corporate rates, new routes, and loyalty programs.',
    date: 'Dec 20, 2024',
    endDate: 'Dec 20, 2024',
    location: 'New York, NY',
    category: 'Partner Event',
    industry: ['Travel', 'Corporate'],
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?auto=format&fit=crop&w=600&q=80',
    attendeeCount: 156,
    interested: false,
    featured: false
  }, {
    id: 'EVT-006',
    title: 'Healthcare IT Summit',
    description: 'Digital health transformation, EHR integration, and healthcare data security best practices.',
    date: 'Mar 12, 2025',
    endDate: 'Mar 14, 2025',
    location: 'San Diego, CA',
    category: 'Summit',
    industry: ['Healthcare', 'Technology'],
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80',
    attendeeCount: 267,
    interested: false,
    featured: false
  }]);
  const [paymentInfo] = useState<PaymentInfo>({
    availableCredit: 75000,
    usedCredit: 125000,
    creditLimit: 200000,
    pendingPayments: 12500,
    totalSpend: 482500
  });
  const [integrations] = useState<Integration[]>([{
    id: 'INT-001',
    name: 'Amadeus',
    type: 'booking_engine',
    status: 'connected',
    lastSync: '5 min ago',
    logo: '✈️'
  }, {
    id: 'INT-002',
    name: 'Sabre',
    type: 'booking_engine',
    status: 'disconnected',
    lastSync: 'Never',
    logo: '🌐'
  }, {
    id: 'INT-003',
    name: 'Travelport',
    type: 'booking_engine',
    status: 'configuring',
    lastSync: '2 days ago',
    logo: '🔗'
  }, {
    id: 'INT-004',
    name: 'Proprietary Engine',
    type: 'booking_engine',
    status: 'connected',
    lastSync: '1 min ago',
    logo: '⚙️'
  }, {
    id: 'INT-005',
    name: 'Uber for Business',
    type: 'ground_transport',
    status: 'connected',
    lastSync: '10 min ago',
    logo: '🚗'
  }, {
    id: 'INT-006',
    name: 'Bolt',
    type: 'ground_transport',
    status: 'disconnected',
    lastSync: 'Never',
    logo: '⚡'
  }, {
    id: 'INT-007',
    name: 'Local Transport Co.',
    type: 'ground_transport',
    status: 'configuring',
    lastSync: '1 day ago',
    logo: '🚐'
  }, {
    id: 'INT-008',
    name: 'InDrive',
    type: 'ground_transport',
    status: 'connected',
    lastSync: '15 min ago',
    logo: '🚙'
  }]);
  const [refundPolicies] = useState<RefundPolicy[]>([{
    id: 'POL-001',
    service: 'Flights',
    refundable: true,
    cancellationRules: 'Free cancellation up to 24 hours before departure. After that, cancellation fee applies.',
    penalties: '$150 cancellation fee after 24h window. No refund for no-shows.',
    timeLimit: '24 hours before departure',
    conditions: 'Refundable with penalty. Full refund within 24h of booking if booked 7+ days before departure.'
  }, {
    id: 'POL-002',
    service: 'Hotels',
    refundable: true,
    cancellationRules: 'Free cancellation up to 48 hours before check-in. Late cancellation incurs one night charge.',
    penalties: 'One night room charge for late cancellation. No refund for early checkout.',
    timeLimit: '48 hours before check-in',
    conditions: 'Conditional cancellation. Prepaid rates are non-refundable.'
  }, {
    id: 'POL-003',
    service: 'Insurance',
    refundable: false,
    cancellationRules: 'Travel insurance is non-refundable once purchased.',
    penalties: 'Full premium forfeited upon cancellation.',
    timeLimit: 'Non-refundable',
    conditions: 'Non-refundable. Coverage begins at time of purchase.'
  }, {
    id: 'POL-004',
    service: 'Ground Transport',
    refundable: true,
    cancellationRules: 'Free cancellation up to 2 hours before pickup. Late cancellation incurs 50% charge.',
    penalties: '50% charge for cancellation within 2 hours. Full charge for no-shows.',
    timeLimit: '2 hours before pickup',
    conditions: 'Refundable with conditions. Airport transfers have stricter policies.'
  }, {
    id: 'POL-005',
    service: 'Ancillary',
    refundable: false,
    cancellationRules: 'Ancillary services (seat upgrades, extra baggage, lounge access) are non-refundable.',
    penalties: 'No refund available.',
    timeLimit: 'Non-refundable',
    conditions: 'Non-refundable once added to booking.'
  }]);
  const [delegations] = useState<Delegation[]>([{
    id: 'DEL-001',
    approver: 'Alex Morgan',
    delegate: 'Mike Ross',
    startDate: 'Nov 15, 2024',
    endDate: 'Nov 22, 2024',
    active: true
  }]);
  const [escalationRules] = useState<EscalationRules>({
    warningHours: 12,
    escalateHours: 24,
    autoEscalateEnabled: true
  });
  const operationsQueue: OperationsQueue = {
    pendingApprovals: travelRequests.filter(r => r.status === 'pending').length,
    awaitingTicketing: 4,
    ticketed: 12,
    refunds: 2,
    cancellations: 1
  };
  // Actions
  const submitBooking = useCallback((booking: any) => {
    const newRequest: TravelRequest = {
      id: `REQ-2024-${Math.floor(Math.random() * 1000)}`,
      requester: 'Current User',
      role: 'Employee',
      trip: booking.name || booking.model || booking.airline || 'New Trip',
      destination: booking.destination || 'San Francisco, CA',
      dates: 'Upcoming',
      amount: booking.price,
      type: booking.type || 'flight',
      details: booking.name || booking.model || booking.airline,
      status: 'pending',
      policyStatus: booking.policy === 'compliant' ? 'compliant' : 'violation',
      policyFlags: booking.policy === 'violation' ? ['Policy violation detected'] : [],
      submittedAt: 'Just now',
      chain: [{
        role: 'Direct Manager',
        name: 'Sarah Connor',
        status: 'pending'
      }, {
        role: 'Dept Head',
        name: 'Alex Morgan',
        status: 'waiting'
      }, {
        role: 'Finance',
        name: 'Emily Chen',
        status: 'waiting'
      }]
    };
    setTravelRequests(prev => [newRequest, ...prev]);
    const newNotification: Notification = {
      id: Date.now(),
      type: 'booking',
      title: 'New booking submitted',
      desc: `Booking for ${newRequest.trip} submitted for approval`,
      time: 'Just now',
      read: false,
      action: 'View Request',
      category: 'operational'
    };
    setNotifications(prev => [newNotification, ...prev]);
  }, []);
  const approveRequest = useCallback((requestId: string) => {
    setTravelRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      const newChain = [...req.chain];
      const pendingIndex = newChain.findIndex(step => step.status === 'pending');
      if (pendingIndex !== -1) {
        newChain[pendingIndex] = {
          ...newChain[pendingIndex],
          status: 'approved'
        };
        if (pendingIndex + 1 < newChain.length) {
          newChain[pendingIndex + 1] = {
            ...newChain[pendingIndex + 1],
            status: 'pending'
          };
        }
      }
      const allApproved = newChain.every(step => step.status === 'approved');
      const newStatus = allApproved ? 'approved' : 'pending';
      if (allApproved && req.status !== 'approved') {
        const newTrip: ConfirmedTrip = {
          id: Date.now(),
          title: req.trip,
          location: req.destination,
          dates: req.dates,
          status: 'Confirmed',
          totalCost: `$${req.amount}`
        };
        setConfirmedTrips(prevTrips => [newTrip, ...prevTrips]);
        const newNotification: Notification = {
          id: Date.now(),
          type: 'approval',
          title: 'Trip Approved',
          desc: `Your trip to ${req.destination} has been fully approved`,
          time: 'Just now',
          read: false,
          action: 'View Trip',
          category: 'operational'
        };
        setNotifications(prevNotifs => [newNotification, ...prevNotifs]);
      }
      return {
        ...req,
        chain: newChain,
        status: newStatus
      };
    }));
  }, []);
  const rejectRequest = useCallback((requestId: string) => {
    setTravelRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      return {
        ...req,
        status: 'rejected'
      };
    }));
    const newNotification: Notification = {
      id: Date.now(),
      type: 'approval',
      title: 'Trip Rejected',
      desc: 'A travel request has been rejected',
      time: 'Just now',
      read: false,
      action: 'View Request',
      category: 'operational'
    };
    setNotifications(prev => [newNotification, ...prev]);
  }, []);
  const markNotificationRead = useCallback((id: number) => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  }, []);
  const markAllNotificationsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      read: true
    })));
  }, []);
  const addAuditLog = useCallback((log: Omit<AuditLog, 'id' | 'timestamp' | 'ip'>) => {
    // In production this would be server-side
  }, []);
  const toggleEventInterest = useCallback((eventId: string) => {
    setEvents(prev => prev.map(evt => {
      if (evt.id !== eventId) return evt;
      return {
        ...evt,
        interested: !evt.interested,
        attendeeCount: evt.interested ? evt.attendeeCount - 1 : evt.attendeeCount + 1
      };
    }));
  }, []);
  return <AppContext.Provider value={{
    travelRequests,
    notifications,
    confirmedTrips,
    auditLogs,
    supportCases,
    events,
    paymentInfo,
    integrations,
    refundPolicies,
    delegations,
    escalationRules,
    operationsQueue,
    submitBooking,
    approveRequest,
    rejectRequest,
    markNotificationRead,
    markAllNotificationsRead,
    addAuditLog,
    toggleEventInterest
  }} data-id="element-194">
      {children}
    </AppContext.Provider>;
}
export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}
export { AppContext };